import React, { useEffect, useContext, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { Box, Typography, Stack, Chip } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import JobDescription from "./JobDescription";
import { pageHeroShellSx } from "../../styles/pageModernStyles";

const JobDescriptionPage = () => {
  const theme = useTheme();
  const { contentMargin, isMobile } = useContext(SidebarContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const token = localStorage.getItem("token");

  const session = useMemo(() => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return {
        email: decoded.email,
        username: decoded.username || decoded.name || "Usuario",
        puedeVerTodos: !!decoded.permisoVisualizarJobsTodos,
      };
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate, token]);

  if (!session?.username) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Typography>
          {t("jobDescription.noUser", {
            defaultValue: "No se encontró el usuario. Por favor, inicia sesión.",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        width: "100%",
        maxWidth: 1400,
        minWidth: 0,
        pt: { xs: 1.25, md: 2 },
        px: { xs: 1.25, md: 3 },
        pb: { xs: 10, md: 3 },
        ...contentMargin,
      }}
    >
      <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <DescriptionOutlinedIcon />
            </Box>
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 1.1 }}
              >
                CRM · RRHH
              </Typography>
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  fontSize: { xs: "1.35rem", md: "1.75rem" },
                }}
              >
                {t("jobDescription.pageTitle", {
                  defaultValue: "Documentos de usuario",
                })}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                <Chip
                  size="small"
                  label={session.username}
                  sx={{ fontWeight: 700 }}
                />
                {session.puedeVerTodos ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("jobDescription.viewAll", {
                      defaultValue: "Vista administrador",
                    })}
                    sx={{ fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Box>

      <JobDescription
        currentUser={session.email}
        puedeVerTodos={session.puedeVerTodos}
      />
    </Box>
  );
};

export default JobDescriptionPage;
